'use client';

import { FC, useContext, useEffect, useState } from 'react';
import { RequestSignatureDialogProps } from './RequestSignatureDialog.types';
import {
  Dialog,
  Flex,
  Text,
  Pill,
  Button,
  ButtonGroup,
  Alert,
} from '@/_components';
import { EntityUser, EngagementSignature } from '@/_types/models/questionnaire';
import {
  addSignatureRequest,
  getActiveEntityUsers,
} from '@/_api/client-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const RequestSignatureDialog: FC<RequestSignatureDialogProps> = ({
  isOpen,
  onClose,
  section,
  companyId,
  questionnaireId,
  reloadQuestionnairePreview,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);
  const [entityOwners, setEntityOwners] = useState<EntityUser[]>([]);

  useEffect(() => {
    const fetchEntityOwners = async () => {
      try {
        const response = await getActiveEntityUsers({
          companyId,
          token,
        });
        setEntityOwners(response);
      } catch (error) {
        setEntityOwners([]);
        addToast('error', 'Error fetching entity users');
      }
    };

    fetchEntityOwners();
  }, [companyId, token, addToast]);

  const oneSignaturePerSectionEnabled =
    preferences?.require_one_signature_per_section;
  const hasSignedSignature = section?.engagement_signatures?.some(
    (signature: EngagementSignature) =>
      !!signature.signed_at && signature.signature !== 'Marked Not Needed',
  );
  const [loadingOwnerId, setLoadingOwnerId] = useState<number | null>(null);

  if (!section) return null;

  const getRole = (owner: EntityUser) => {
    if (owner.is_taxpayer) return 'Taxpayer';
    if (owner.is_spouse) return 'Spouse';
    return '';
  };

  const filteredEntityOwners = entityOwners.filter(
    (owner: EntityUser) =>
      !section?.engagement_signatures?.some(
        (signature: EngagementSignature) => signature.user_id === owner.id,
      ),
  );
  const hasEntityOwners = filteredEntityOwners.length > 0;

  const onRequestSignature = async (owner: EntityUser) => {
    setLoadingOwnerId(owner.id);
    try {
      const response = await addSignatureRequest({
        questionnaireId,
        companyId,
        userId: owner.id,
        sectionId: section?.id,
        token,
      });

      if (response.error) {
        addToast('error', response.error);
        return;
      }

      await reloadQuestionnairePreview();
      addToast('success', 'Signature requested');
      onClose();
    } catch (error) {
      addToast('error', 'Error requesting signature');
    } finally {
      setLoadingOwnerId(null);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Request Signature">
      <Dialog.Content>
        <Flex isColumn gap={8}>
          {hasEntityOwners ? (
            <>
              {oneSignaturePerSectionEnabled && hasSignedSignature && (
                <Alert type="warning">
                  One Signature Per Section is enabled. Once a signature is
                  applied, all other signatures will automatically be marked as
                  not needed.
                </Alert>
              )}
              {filteredEntityOwners.map((owner: EntityUser) => {
                const role = getRole(owner);
                const isLoading = loadingOwnerId === owner.id;
                return (
                  <Flex key={owner.id} gap={4} isBetween isYCentered>
                    <Flex gap={4} isColumn>
                      <Flex gap={2} isYCentered>
                        <Text fontWeight="bold">{owner.full_name}</Text>
                        {role && <Pill label={role} intent="secondary" />}
                      </Flex>
                      <Text type="small" color="secondary">
                        {owner.email}
                      </Text>
                    </Flex>
                    <ButtonGroup>
                      <Button
                        size="small"
                        intent="primary"
                        onClick={() => onRequestSignature(owner)}
                        value="Request"
                        isLoading={isLoading}
                        isDisabled={loadingOwnerId !== null && !isLoading}
                      />
                    </ButtonGroup>
                  </Flex>
                );
              })}
            </>
          ) : (
            <Alert type="warning">
              All entity users have already been requested to sign this section.
            </Alert>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
